import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise, GearSix, Plugs, Copy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const DCS = [
  { v: "in", l: "India (zoho.in)" }, { v: "com", l: "US (zoho.com)" }, { v: "eu", l: "Europe (zoho.eu)" },
  { v: "com.au", l: "Australia" }, { v: "jp", l: "Japan" }, { v: "sa", l: "Saudi Arabia" }, { v: "ca", l: "Canada" }];

const Pills = ({ values, selected, onToggle, tone }) => (
  <div className="flex flex-wrap gap-1.5 mt-1.5">
    {values.map((v) => (
      <button key={v} onClick={() => onToggle(v)}
        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition-colors ${
          selected.includes(v)
            ? tone === "pos" ? "border-[#00953e] bg-success/10 text-[#00953e]" : "border-[#c73030] bg-danger/10 text-[#c73030]"
            : "border-[#E5E7EB] text-[#525866] hover:bg-[#F3F4F6]"}`}>
        {v}
      </button>
    ))}
  </div>
);

function ConnectDialog({ open, onClose, redirectUri }) {
  const [cid, setCid] = useState("");
  const [sec, setSec] = useState("");
  const [dc, setDc] = useState("in");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (!cid.trim() || !sec.trim()) { toast.error("Paste both Client ID and Client Secret"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/zoho/connect", { client_id: cid, client_secret: sec, dc });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Zoho sign-in", { duration: 8000 });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="zoho-connect-dialog">
        <DialogHeader><DialogTitle className="font-heading font-extrabold">Connect Zoho CRM</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <ol className="text-xs text-[#525866] space-y-1.5 list-decimal pl-4">
            <li>Open <a href="https://api-console.zoho.in" target="_blank" rel="noreferrer" className="text-[#0052FF] font-bold underline">api-console.zoho.in</a> (use your data center) → <b>Add Client → Server-based Applications</b></li>
            <li>Homepage URL: your website · Authorized Redirect URI — paste exactly:</li>
          </ol>
          <button onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Redirect URI copied"); }}
            className="w-full flex items-center gap-2 rounded-md border border-dashed border-[#c9cdd6] bg-[#FAFAFB] px-3 py-2 text-[11px] font-mono text-[#0A0D14] hover:bg-[#F3F4F6]" data-testid="zoho-redirect-uri">
            <Copy size={13} className="shrink-0 text-[#0052FF]" /> <span className="truncate">{redirectUri}</span>
          </button>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-bold text-[#0A0D14]">Client ID</label>
              <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="1000.XXXXXXXXXX"
                className="mt-1 h-9 text-xs font-mono" data-testid="zoho-client-id" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#0A0D14]">Client Secret</label>
              <Input value={sec} onChange={(e) => setSec(e.target.value)} type="password" placeholder="••••••••••••"
                className="mt-1 h-9 text-xs font-mono" data-testid="zoho-client-secret" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#0A0D14]">Data center (where your Zoho account lives)</label>
              <Select value={dc} onValueChange={setDc}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="zoho-dc-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DCS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={go} disabled={busy} className="w-full rounded-full bg-[#E42527] hover:bg-[#c41f21] text-white font-bold h-10" data-testid="zoho-authorize-btn">
            {busy ? "Redirecting to Zoho…" : "Sign in with Zoho & authorize"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ open, onClose, status, onDone }) {
  const [module, setModule] = useState("Leads");
  const [fields, setFields] = useState(null);
  const [field, setField] = useState("");
  const [hotField, setHotField] = useState("none");
  const [pos, setPos] = useState([]);
  const [neg, setNeg] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModule(status?.module || "Leads");
    setField(status?.status_field || "");
    setHotField(status?.hot_status_field || "none");
    setPos(status?.positive_values || []);
    setNeg(status?.negative_values || []);
    setQ("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setFields(null);
    api.get(`/zoho/fields?module=${module}`).then(({ data }) => setFields(data.fields)).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not load Zoho fields", { duration: 8000 });
      setFields([]);
    });
  }, [open, module]);

  const values = (fields || []).find((f) => f.api_name === field)?.values || [];
  const ql = q.trim().toLowerCase();
  const sortSelectedFirst = (selected) => (a, b) => {
    const sa = selected.includes(a) ? 0 : 1;
    const sb = selected.includes(b) ? 0 : 1;
    return sa - sb || a.localeCompare(b);
  };
  const posShown = values.filter((v) => !ql || v.toLowerCase().includes(ql) || pos.includes(v)).sort(sortSelectedFirst(pos));
  const negPool = values.filter((v) => !pos.includes(v));
  const negShown = negPool.filter((v) => !ql || v.toLowerCase().includes(ql) || neg.includes(v)).sort(sortSelectedFirst(neg));

  const save = async () => {
    if (!field) { toast.error("Pick the status field your sales team fills"); return; }
    if (pos.length === 0) { toast.error("Select at least one value that counts as positive"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/zoho/settings", { module, status_field: field, hot_status_field: hotField === "none" ? "" : hotField, positive_values: pos, negative_values: neg });
      toast.success(`Synced ${data.records ?? 0} CRM records — ${data.matched ?? 0} matched with Facebook leads`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save Zoho settings", { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="zoho-config-dialog">
        <DialogHeader><DialogTitle className="font-heading font-extrabold">Zoho sync settings</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm overflow-y-auto pr-1 flex-1 min-h-0">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold text-[#0A0D14]">CRM module</label>
              <Select value={module} onValueChange={(v) => { setModule(v); setField(""); setPos([]); setNeg([]); }}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="zoho-module-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Leads">Leads</SelectItem><SelectItem value="Contacts">Contacts</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-bold text-[#0A0D14]">Status field</label>
              <Select value={field} onValueChange={(v) => { setField(v); setPos([]); setNeg([]); }}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="zoho-field-select">
                  <SelectValue placeholder={fields === null ? "Loading fields…" : "Pick field"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(fields || []).map((f) => <SelectItem key={f.api_name} value={f.api_name}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#0A0D14]">Hot-lead field <span className="font-normal text-[#8a90a0]">(optional — if "Hot" lives in a different field)</span></label>
            <Select value={hotField} onValueChange={setHotField}>
              <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="zoho-hot-field-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">None — same as status field</SelectItem>
                {(fields || []).filter((f) => f.api_name !== field).map((f) => <SelectItem key={f.api_name} value={f.api_name}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#8a90a0] mt-1">Example: admissions come from "Sales user lead status", but inside sales marks Hot in "Lead Status" — pick "Lead Status" here. Its values then appear in the Conversions API Hot Leads selector.</p>
          </div>
          {fields !== null && fields.length === 0 && (
            <p className="text-[11px] text-[#c73030]">No picklist fields found in this module — the status field must be a dropdown (picklist) in Zoho.</p>
          )}
          {values.length > 0 && (
            <>
              {values.length > 12 && (
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${values.length} status values… (selected ones stay pinned on top)`}
                  className="h-9 text-xs" data-testid="zoho-value-search" />
              )}
              <div>
                <label className="text-xs font-bold text-[#00953e]">✓ Positive (admission / sale) when status is: <span className="text-[#8a90a0] font-semibold">{pos.length} selected</span></label>
                <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-[#E5E7EB] bg-[#FAFAFB] p-2" data-testid="zoho-positive-box">
                  <Pills values={posShown} selected={pos} tone="pos"
                    onToggle={(v) => { setPos((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]); setNeg((n) => n.filter((x) => x !== v)); }} />
                  {posShown.length === 0 && <p className="text-[11px] text-[#8a90a0] px-1 py-1.5">No values match "{q}"</p>}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#c73030]">✗ Negative when status is: <span className="text-[#8a90a0] font-semibold">{neg.length} selected (optional — rest stay "in follow-up")</span></label>
                <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-[#E5E7EB] bg-[#FAFAFB] p-2" data-testid="zoho-negative-box">
                  <Pills values={negShown} selected={neg} tone="neg"
                    onToggle={(v) => setNeg((n) => n.includes(v) ? n.filter((x) => x !== v) : [...n, v])} />
                  {negShown.length === 0 && <p className="text-[11px] text-[#8a90a0] px-1 py-1.5">No values match "{q}"</p>}
                </div>
              </div>
            </>
          )}
          <Button onClick={save} disabled={busy} className="w-full rounded-full bg-[#0A0D14] hover:bg-black text-white font-bold h-10" data-testid="zoho-save-btn">
            {busy ? "Syncing with Zoho…" : "Save & sync now"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ZohoCard = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/zoho/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const z = params.get("zoho");
    if (!z) return;
    if (z === "connected") { toast.success("Zoho CRM connected — now choose your status field"); setShowConfig(true); }
    else if (z === "cancelled") toast.info("Zoho connection cancelled");
    else toast.error(params.get("reason") || "Zoho connection failed", { duration: 9000 });
    params.delete("zoho"); params.delete("reason");
    setParams(params, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/zoho/sync");
      toast.success(`Synced ${data.records} CRM records — ${data.matched} matched with Facebook leads`);
      load(); onSynced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zoho sync failed", { duration: 8000 });
    } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete("/zoho/disconnect");
      toast.success("Zoho disconnected — CRM-sourced outcomes removed");
      load(); onSynced?.();
    } catch { toast.error("Could not disconnect"); }
  };

  const fmtSync = (s) => {
    const d = new Date(s);
    return isNaN(d) ? "—" : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const connected = status?.connected;
  const configured = status?.configured;

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-5" data-testid="zoho-source-card">
      <ConnectDialog open={showConnect} onClose={() => setShowConnect(false)} redirectUri={status?.redirect_uri || ""} />
      <ConfigDialog open={showConfig} onClose={() => setShowConfig(false)} status={status}
        onDone={() => { setShowConfig(false); load(); onSynced?.(); }} />
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-lg bg-[#E42527]/10 flex items-center justify-center font-heading font-extrabold text-[#E42527]">Z</span>
        <div className="mr-auto min-w-0">
          <div className="font-bold text-sm text-[#0A0D14]">Zoho CRM Plus {status?.org_name && status.org_name.length > 1 ? <span className="text-[#8a90a0] font-semibold">· {status.org_name}</span> : null}</div>
          <div className="text-[11px] text-[#8a90a0] truncate" data-testid="zoho-status-line">
            {!status ? "Checking connection…"
              : !connected ? "Auto-sync sales statuses straight from your CRM every 3 hours"
              : !configured ? "Connected — choose the status field to start syncing"
              : `${status.module} · ${status.status_field}${status.last_sync ? ` · last sync ${fmtSync(status.last_sync)} — ${status.last_matched ?? 0} of ${status.last_records ?? 0} matched` : " · not synced yet"}`}
          </div>
        </div>
        {!connected ? (
          <button onClick={() => setShowConnect(true)} data-testid="zoho-connect-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#E42527] text-white text-[11px] font-bold px-3.5 py-2 hover:bg-[#c41f21]">
            <Plugs size={13} weight="bold" /> Connect Zoho
          </button>
        ) : (
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={configured ? syncNow : () => setShowConfig(true)} disabled={syncing} data-testid="zoho-sync-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0D14] text-white text-[11px] font-bold px-3.5 py-2 hover:bg-black disabled:opacity-50">
              <ArrowsClockwise size={13} weight="bold" className={syncing ? "animate-spin" : ""} />
              {configured ? (syncing ? "Syncing…" : "Sync now") : "Set up sync"}
            </button>
            <button onClick={() => setShowConfig(true)} title="Sync settings" data-testid="zoho-config-btn"
              className="h-8 w-8 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#525866] hover:bg-[#F3F4F6]">
              <GearSix size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
      {connected && (
        <div className="mt-3 flex items-center justify-between">
          {status?.positive_values?.length > 0 ? (
            <p className="text-[11px] text-[#8a90a0]">Positive: <b className="text-[#00953e]">{status.positive_values.join(", ")}</b>{status.negative_values?.length ? <> · negative: <b className="text-[#c73030]">{status.negative_values.join(", ")}</b></> : null}</p>
          ) : <span />}
          <button onClick={disconnect} className="text-[10px] font-bold text-[#8a90a0] hover:text-[#c73030] underline" data-testid="zoho-disconnect-btn">Disconnect</button>
        </div>
      )}
    </div>
  );
};
